import { TRANSPORT_SETTINGS_RANGES, type TransportSettingKey, type TransportSettings, makePatchTransportSettingsRequest } from "@seagames/contracts";
import { Field, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { invalidateFleet, patchTransportSettings, transportSettingsOptions } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { formatShort, formatDateTime } from "../../lib/format/datetime";
import { inputClass, useProblemText } from "./form-bits";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type Problems, hasProblems, issuesToProblems } from "./model";

const KEYS = Object.keys(TRANSPORT_SETTINGS_RANGES) as TransportSettingKey[];

/**
 * Transport settings for one games (`transport_settings`, PLAN §2.1): the
 * default drive time, the NOC booking lead (hard for ORGANIZATION logins),
 * the buffer a one-way venue trip holds the vehicle for, and how far a
 * delayed leg may drift from its transfer before it is detached. Ranges and
 * defaults from contracts (the DB CHECKs are built from the same numbers).
 */
export function SettingsModal({ gamesId, onClose }: { gamesId: string; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const settings = useQuery(transportSettingsOptions(gamesId));
	return settings.isPending ? (
		<Modal open onClose={onClose} size="md" title={t("settings.title")}>
			<div className="skeleton h-[14rem] rounded-[0.625rem]" />
		</Modal>
	) : settings.isError ? (
		<Modal open onClose={onClose} size="md" title={t("settings.title")}>
			<p role="alert" className="rounded-field bg-error/8 px-[0.875rem] py-[0.75rem] text-sm">
				{errorMessage(settings.error)}
			</p>
		</Modal>
	) : (
		<SettingsForm key={settings.data.updated_at} settings={settings.data} onClose={onClose} />
	);
}

function SettingsForm({ settings, onClose }: { settings: TransportSettings; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const problemText = useProblemText();
	const messages = useFleetFormMessages();
	const [draft, setDraft] = useState<Record<TransportSettingKey, string>>(() => Object.fromEntries(KEYS.map((k) => [k, String(settings[k])])) as Record<TransportSettingKey, string>);
	const [problems, setProblems] = useState<Problems<TransportSettingKey>>({});
	const [busy, setBusy] = useState(false);

	const save = async (e: React.FormEvent) => {
		e.preventDefault();
		const changed = KEYS.filter((k) => draft[k].trim() !== String(settings[k]));
		if (changed.length === 0) {
			onClose();
			return;
		}
		const found: Problems<TransportSettingKey> = {};
		for (const k of changed) {
			const r = TRANSPORT_SETTINGS_RANGES[k];
			const n = Number(draft[k]);
			if (!/^\d+$/.test(draft[k].trim()) || n < r.min || n > r.max) found[k] = { key: "settingRange", values: { min: formatNumber(r.min), max: formatNumber(r.max) } };
		}
		setProblems(found);
		if (hasProblems(found)) return;
		const body = Object.fromEntries(changed.map((k) => [k, Number(draft[k])]));
		const parsed = makePatchTransportSettingsRequest(messages).safeParse({ ...body, reason: `Changed transport settings: ${changed.map((k) => k.replace(/_/g, " ")).join(", ")}` });
		if (!parsed.success) {
			setProblems(issuesToProblems(parsed.error.issues, KEYS, changed[0] ?? "default_drive_minutes"));
			return;
		}
		setBusy(true);
		try {
			await patchTransportSettings(settings.games_id, parsed.data);
			await invalidateFleet();
			toast.success(t("settings.saved"));
			onClose();
		} catch (err) {
			toast.error(err, t("settings.saveFailed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={t("settings.title")}
			description={t("settings.description")}
			dismissible={!busy}
			footer={
				<ModalActions start={<span className="text-[0.75rem] text-gms-ink-3" title={formatDateTime(settings.updated_at)}>{t("settings.updated", { stamp: formatShort(settings.updated_at) })}</span>}>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="submit" form="ts-form" className="btn btn-primary btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("settings.save")}
					</button>
				</ModalActions>
			}
		>
			<form id="ts-form" onSubmit={(e) => void save(e)} noValidate className="grid gap-[0.875rem] sm:grid-cols-2">
				{KEYS.map((k) => {
					const r = TRANSPORT_SETTINGS_RANGES[k];
					const unit = k === "noc_lead_hours" ? t("settings.hours") : t("settings.minutes");
					return (
						<Field key={k} label={t(`settings.field.${k}`)} htmlFor={`ts-${k}`} required error={problemText(problems[k])} hint={t(`settings.hint.${k}`, { n: formatNumber(r.default) })}>
							<label className={inputClass(!!problems[k])}>
								<input
									id={`ts-${k}`}
									type="number"
									inputMode="numeric"
									min={r.min}
									max={r.max}
									step={1}
									value={draft[k]}
									onChange={(e) => {
										setDraft((d) => ({ ...d, [k]: e.target.value }));
										setProblems((p) => ({ ...p, [k]: undefined }));
									}}
									className="grow tabular-nums"
								/>
								<span className="text-[0.75rem] text-gms-ink-3">{unit}</span>
							</label>
						</Field>
					);
				})}
			</form>
		</Modal>
	);
}
